import { useEffect, useMemo, useState } from "react";
import {
  getAvailableFreeDoubtsCount,
  useGetDc1AvailableFreeDoubts,
  useGetDc1AvailableFreeDoubtsFromSource,
  useStudentsControllerGetUsersSubscriptionStatus,
} from "@oneapp/data";
import type { DoubtSession } from "./doubtSession";

/** Dashboard uses generic DC quota; AINA camera flow uses `/dc/1/availableFreeDoubtsFromSource`. */
export type DoubtsAccessQuotaMode = "dashboard" | "aina";

const AINA_FREE_DOUBTS_DC_SOURCE = "AINA";

const readNumber = (value: unknown): number | undefined => {
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

export function parseSubscriptionStatus(payload: unknown) {
  const response = payload as Record<string, unknown> | undefined;
  const responseData = response?.data as Record<string, unknown> | undefined;
  const nestedData = responseData?.data as Record<string, unknown> | undefined;

  const source = nestedData ?? responseData ?? response;

  const subscribeStatus =
    typeof source?.subscribeStatus === "boolean"
      ? source.subscribeStatus
      : false;

  const freeCounts = readNumber(source?.freeCounts);

  return {
    subscribeStatus,
    freeCounts,
  };
}

export type DoubtsAccessSession = DoubtSession;

export function useDoubtsAccessExhaustion(
  session: DoubtsAccessSession,
  options?: { quotaMode?: DoubtsAccessQuotaMode },
) {
  const quotaMode = options?.quotaMode ?? "dashboard";
  const userId = session.uamId;
  const dcNumericTenantId = session.dcNumericTenantId;

  const dashboardQuotaEnabled =
    quotaMode === "dashboard" && !!userId && !!dcNumericTenantId;
  const ainaQuotaEnabled =
    quotaMode === "aina" && !!userId && !!dcNumericTenantId;

  const { data: dashboardFreeData, isLoading: dashboardFreeLoading } =
    useGetDc1AvailableFreeDoubts(
      {
        userId: String(userId ?? ""),
        tenantId: dcNumericTenantId ?? "",
      },
      {
        request: { clientName: "doubtsAi" },
        query: { enabled: dashboardQuotaEnabled },
      },
    );

  const { data: ainaFreeData, isLoading: ainaFreeLoading } =
    useGetDc1AvailableFreeDoubtsFromSource(
      {
        userId: String(userId ?? ""),
        tenantId: dcNumericTenantId ?? "",
        source: AINA_FREE_DOUBTS_DC_SOURCE,
      },
      {
        request: { clientName: "doubtsDCAPI" },
        query: { enabled: ainaQuotaEnabled },
      },
    );

  const freeDoubtsData =
    quotaMode === "dashboard" ? dashboardFreeData : ainaFreeData;
  const isFreeDoubtsLoading =
    quotaMode === "dashboard" ? dashboardFreeLoading : ainaFreeLoading;

  const { mutateAsync: getSubscriptionStatus } =
    useStudentsControllerGetUsersSubscriptionStatus({
      request: {
        clientName: "oneApp",
        headers: session.accessToken
          ? {
              Acesstoken: session.accessToken,
              Authorization: `Bearer ${session.accessToken}`,
              "x-tenant": session.tenantId ?? "infinitylearn",
              subTenantId: String(session.subtenantId ?? ""),
            }
          : undefined,
      },
    });

  const [subscriptionStatus, setSubscriptionStatus] = useState(false);
  const [subscriptionFreeCounts, setSubscriptionFreeCounts] = useState<
    number | null
  >(null);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);

  useEffect(() => {
    setSubscriptionChecked(false);

    if (!session.programId || !(session.mdsGradeId ?? session.gradeId)) {
      setSubscriptionStatus(false);
      setSubscriptionFreeCounts(null);
      setSubscriptionChecked(true);
      return;
    }

    let isMounted = true;

    const fetchSubscriptionStatus = async () => {
      try {
        const basePayload = {
          programId: session.programId ?? "",
          module: "doubts" as const,
          grade: Number(session.mdsGradeId ?? session.gradeId ?? 0),
          testseriesType: "",
          classId: "",
          programIds: [] as string[],
        };
        const response = await getSubscriptionStatus({
          data: {
            ...basePayload,
            ...(session.mdsExamId != null && session.mdsExamId > 0
              ? { mdsExamId: session.mdsExamId }
              : {}),
          } as Parameters<typeof getSubscriptionStatus>[0]["data"],
        });

        if (!isMounted) return;

        const parsed = parseSubscriptionStatus(response);
        setSubscriptionStatus(parsed.subscribeStatus);
        setSubscriptionFreeCounts(parsed.freeCounts ?? null);
        setSubscriptionChecked(true);
      } catch {
        if (!isMounted) return;
        setSubscriptionStatus(false);
        setSubscriptionFreeCounts(null);
        setSubscriptionChecked(true);
      }
    };

    void fetchSubscriptionStatus();

    return () => {
      isMounted = false;
    };
  }, [
    getSubscriptionStatus,
    session.gradeId,
    session.mdsGradeId,
    session.mdsExamId,
    session.programId,
    session.accessToken,
    session.tenantId,
    session.dcNumericTenantId,
    session.subtenantId,
  ]);

  const availableFreeDoubts = useMemo(
    () => getAvailableFreeDoubtsCount(freeDoubtsData),
    [freeDoubtsData],
  );

  const hasSubscriptionDoubtsAccess =
    subscriptionStatus && (subscriptionFreeCounts ?? 0) > 0;

  const isAccessCheckPending =
    (Boolean(userId) && isFreeDoubtsLoading) || !subscriptionChecked;

  const isExhausted =
    !isAccessCheckPending &&
    !(availableFreeDoubts > 0 || hasSubscriptionDoubtsAccess);

  return {
    isExhausted,
    isAccessCheckPending,
    availableFreeDoubts,
    subscribeStatus: subscriptionStatus,
    subscriptionFreeCounts,
    hasSubscriptionDoubtsAccess,
  };
}
